import { useCallback, useEffect, useRef, useState } from "react";
import { Download, Loader2, FileArchive } from "lucide-react";
import { toast } from "sonner";
import { API, authHeaders } from "../lib/supabase";
import { useOnline } from "../lib/net";

const fetchStatus = async () => {
  const r = await fetch(`${API}/exports`, { headers: await authHeaders() });
  if (!r.ok) throw new Error("export status unavailable");
  return r.json();
};
const fmtSize = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const fmtDate = (iso) => new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
const hoursLeft = (iso) => Math.max(1, Math.ceil((new Date(iso) - Date.now()) / 3600000));

/* Settings → "Download my data": request → preparing (poll 10 s + refetch on focus) → ready (download via 10-min signed URL). */
export const DataExportCard = () => {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const online = useOnline();
  const ref = useRef(null);
  const refresh = useCallback(() => fetchStatus().then(setSt).catch(() => {}), []);

  useEffect(() => { refresh(); window.addEventListener("focus", refresh); return () => window.removeEventListener("focus", refresh); }, [refresh]);
  useEffect(() => { if (!st?.in_flight) return undefined; const id = setInterval(refresh, 10000); return () => clearInterval(id); }, [st?.in_flight, refresh]);
  useEffect(() => { if (window.location.hash === "#export") setTimeout(() => ref.current?.scrollIntoView({ block: "center" }), 150); }, []);

  const request = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/exports`, { method: "POST", headers: await authHeaders() });
      const body = await r.json().catch(() => ({}));
      if (r.status === 429) toast.error(body.in_flight ? "An export is already being prepared" : `Next export available in ${hoursLeft(body.next_available_at)}h`);
      else if (!r.ok) throw new Error(body.detail || "Could not start the export");
      else toast.success("Preparing your export — we'll email you when it's ready");
      await refresh();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const download = async (id) => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/exports/${id}/download`, { headers: { Accept: "application/json", ...(await authHeaders()) } });
      if (!r.ok) throw new Error(r.status === 404 ? "This export has expired — request a new one" : "Download unavailable right now");
      const { url } = await r.json();
      const a = document.createElement("a"); a.href = url; a.download = `dropit-data-export-${id.slice(0, 8)}.zip`; a.rel = "noopener"; document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const ready = st?.ready;
  const failed = st?.latest?.status === "failed";
  const disabled = busy || !online || !st || !st.can_request;
  const hint = !online ? "You're offline — reconnect to request an export" : st && !st.in_flight && !st.can_request ? `Next export available in ${hoursLeft(st.next_available_at)}h` : null;
  return (
    <section className="card fade-up" id="export" ref={ref} data-testid="data-export-card" data-state={st?.in_flight ? "preparing" : ready ? "ready" : "idle"}>
      <p className="eyebrow">Download my data</p>
      <p className="font-bold mt-1 flex items-center gap-2"><FileArchive size={16} className="text-primary" /> A copy of everything in your account</p>
      <p className="text-sm text-muted mt-1">Jobs, sites, crew, wallet and payment history, consent records, report PDFs and job media — as JSON, CSV and files in one ZIP. One export per 24 hours; the file is kept for 7 days.</p>
      {st?.in_flight && <p className="text-sm mt-3 flex items-center gap-2" data-testid="data-export-preparing"><Loader2 size={16} className="animate-spin text-primary" /> Preparing your export… we'll email you when it's ready (usually a few minutes).</p>}
      {failed && !st?.in_flight && <p className="text-sm mt-3 text-[#FF7A7A]" data-testid="data-export-failed">Your last export failed{st.latest.error ? ` (${st.latest.error})` : ""}. Please try again.</p>}
      {ready && (
        <div className="mt-3 flex flex-wrap items-center gap-3" data-testid="data-export-ready">
          <p className="text-sm">Export ready · <span className="notranslate" translate="no">{fmtSize(ready.size_bytes || 0)}</span> · expires <span className="notranslate" translate="no">{fmtDate(ready.expires_at)}</span></p>
          <button type="button" className="btn-primary !w-auto px-4 inline-flex items-center gap-2" disabled={busy || !online} onClick={() => download(ready.id)} data-testid="data-export-download-button"><Download size={16} /> Download</button>
        </div>
      )}
      <button type="button" className="btn-secondary mt-4" disabled={disabled} onClick={request} data-testid="data-export-request-button">{busy ? "Working…" : ready ? "Request a new export" : "Request my data export"}</button>
      {hint && <p className="text-xs text-muted mt-2" data-testid="data-export-hint">{hint}</p>}
    </section>
  );
};
